import * as Sentry from "@sentry/react";
import {
  useSurfacePreferences,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { BrowserDeniedNotice } from "@/components/notifications/BrowserDeniedNotice.tsx";
import { canGetIphoneApp, GetIphoneApp } from "@/components/notifications/GetIphoneApp.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useNotification } from "@/hooks/useBrowserNotifications/NotificationContext.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import {
  ESCALATION_FIRST_DELAY_OPTIONS,
  ESCALATION_SECOND_DELAY_OPTIONS,
  HALF_HOUR_TIMES,
  type NotificationSelectOption,
} from "@/shared/notifications/options.ts";
import { useGateValue } from "@statsig/react-bindings";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  isReminderChannelOn,
  isReminderChannelReachable,
  REMINDER_CHANNEL_LABELS,
  REMINDER_SETTINGS_CHANNELS,
  REMINDER_SETTINGS_DESCRIPTION,
  reminderChannelDelta,
  reminderChannelHint,
} from "@/shared/reminderChannels.ts";
import {
  ROUTINE_NOTIFICATION_CHANNELS,
  ROUTINE_NOTIFICATIONS_DESCRIPTION,
  type RoutineNotificationChannel,
  routineNotificationDelta,
} from "@/shared/notifications/routineChannels.ts";
import { notificationWhatsappHint } from "@/shared/notifications/whatsapp.ts";
import { pushPromptQuery } from "@/shared/pushPromptQueries.ts";
import { reminderQueries } from "@/shared/reminderQueries.ts";
import { BILLING_GATE } from "@/types/billing.ts";
import { REMINDER_CHANNELS_GATE } from "@/types/calendarEvent.ts";
import type { ReminderChannel } from "@/types/calendarEvent.ts";
import type { UserPreferencesRequired } from "@/types/userPreferences.ts";
import { EmailSubscriptions } from "./EmailSubscriptions.tsx";
import { SettingsRow } from "./SettingsRow.tsx";

const BrowserDeniedRow = () => (
  <div className="py-4 border-b border-border last:border-b-0">
    <div className="text-sm font-medium mb-2">Browser notifications</div>
    <BrowserDeniedNotice />
  </div>
);

// Option tables live in shared/client so native's selects can't drift.
type Option = NotificationSelectOption;

// A compact dropdown. `disabledValue` greys out one option (used to keep the
// two quiet-hours times from being set to the same value).
const OptionSelect = ({
  ariaLabel,
  value,
  options,
  disabled,
  disabledValue,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  options: Option[];
  disabled?: boolean;
  disabledValue?: string;
  onChange: (value: string) => void;
}) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger aria-label={ariaLabel} className="w-32 bg-white rounded-lg border">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((option) => (
        <SelectItem
          key={option.value}
          value={option.value}
          disabled={option.value === disabledValue}
        >
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * A switch per channel okthx may send the user's reminders on. A channel that
 * can't reach them yet keeps its switch, with a hint saying what it needs.
 */
const ReminderChannelRows = ({
  notifications,
  hasVerifiedPhone,
  pendingKeys,
  onToggle,
}: {
  notifications: UserPreferencesRequired["notifications"];
  hasVerifiedPhone: boolean;
  pendingKeys: ReadonlySet<string>;
  onToggle: (channel: ReminderChannel, on: boolean) => void;
}) => {
  const { data: available } = useQuery(reminderQueries.channels());
  return (
    <fieldset aria-label="Reminders">
      <div className="py-4 border-b border-border">
        <div className="text-sm font-medium">Reminders</div>
        <div className="text-sm text-muted-foreground mt-0.5">{REMINDER_SETTINGS_DESCRIPTION}</div>
      </div>
      <div className="pl-6">
        {REMINDER_SETTINGS_CHANNELS.map((channel) => (
          <SettingsRow
            key={channel}
            title={REMINDER_CHANNEL_LABELS[channel]}
            description={reminderChannelHint(
              channel,
              isReminderChannelReachable(channel, { hasVerifiedPhone, available }),
            )}
            checked={isReminderChannelOn(notifications, channel)}
            disabled={pendingKeys.has(`reminders.${channel}`)}
            onCheckedChange={(next) => onToggle(channel, next)}
          />
        ))}
      </div>
    </fieldset>
  );
};

/** A switch per channel `notifications.NOTIFY` may use: routine results and other updates. */
const RoutineNotificationRows = ({
  notifications,
  pendingKeys,
  onToggle,
}: {
  notifications: UserPreferencesRequired["notifications"];
  pendingKeys: ReadonlySet<string>;
  onToggle: (channel: RoutineNotificationChannel, on: boolean) => void;
}) => (
  <fieldset aria-label="Routine notifications">
    <div className="py-4 border-b border-border">
      <div className="text-sm font-medium">Routine notifications</div>
      <div className="text-sm text-muted-foreground mt-0.5">
        {ROUTINE_NOTIFICATIONS_DESCRIPTION}
      </div>
    </div>
    <div className="pl-6">
      {ROUTINE_NOTIFICATION_CHANNELS.map(({ key, title, description }) => (
        <SettingsRow
          key={key}
          title={title}
          description={description}
          checked={notifications.routineNotifications[key]}
          disabled={pendingKeys.has(`routineNotifications.${key}`)}
          onCheckedChange={(next) => onToggle(key, next)}
        />
      ))}
    </div>
  </fieldset>
);

const QuietHoursRow = ({
  start,
  end,
  startPending,
  endPending,
  onCommit,
}: {
  start: string;
  end: string;
  startPending: boolean;
  endPending: boolean;
  onCommit: (key: "quietHoursStart" | "quietHoursEnd", value: string) => void;
}) => (
  <div className="flex items-center gap-3 pb-4 border-b border-border text-sm text-muted-foreground">
    <span className="flex items-center gap-2">
      From
      <OptionSelect
        ariaLabel="Quiet hours start"
        value={start}
        options={HALF_HOUR_TIMES}
        disabled={startPending}
        disabledValue={end}
        onChange={(value) => onCommit("quietHoursStart", value)}
      />
    </span>
    <span className="flex items-center gap-2">
      To
      <OptionSelect
        ariaLabel="Quiet hours end"
        value={end}
        options={HALF_HOUR_TIMES}
        disabled={endPending}
        disabledValue={start}
        onChange={(value) => onCommit("quietHoursEnd", value)}
      />
    </span>
  </div>
);

const EscalationDelayRow = ({
  first,
  second,
  firstPending,
  secondPending,
  onCommit,
}: {
  first: string;
  second: string;
  firstPending: boolean;
  secondPending: boolean;
  onCommit: (key: "escalationFirstDelay" | "escalationSecondDelay", value: string) => void;
}) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pb-4 border-b border-border text-sm text-muted-foreground">
    <span className="flex items-center gap-2">
      Text me after
      <OptionSelect
        ariaLabel="First reminder delay"
        value={first}
        options={ESCALATION_FIRST_DELAY_OPTIONS}
        disabled={firstPending}
        onChange={(value) => onCommit("escalationFirstDelay", value)}
      />
    </span>
    <span className="flex items-center gap-2">
      then again after
      <OptionSelect
        ariaLabel="Second reminder delay"
        value={second}
        options={ESCALATION_SECOND_DELAY_OPTIONS}
        disabled={secondPending}
        onChange={(value) => onCommit("escalationSecondDelay", value)}
      />
    </span>
  </div>
);

type NotificationTier = "needsAttention" | "fyi";
type NotificationChannel = "push" | "inAppBadge" | "email" | "sms" | "whatsapp";
// The per-tier keys we can POST a delta for: the channels plus the master switch.
type NotificationTierKey = NotificationChannel | "enabled";

// The two tiers, each with a heading and the channels users pick. The schema
// carries the per-channel defaults (push/badge on, email/SMS opt-in).
const TIER_SECTIONS: {
  tier: NotificationTier;
  title: string;
  description: string;
}[] = [
  {
    tier: "needsAttention",
    title: "When I need you",
    description: "Questions and approvals that are waiting on you.",
  },
  {
    tier: "fyi",
    title: "Heads-up",
    description: "Updates when work finishes.",
  },
];

const TierChannels = ({
  tier,
  values,
  hasVerifiedPhone,
  whatsapp,
  pendingKeys,
  groupDisabled,
  onToggle,
}: {
  tier: NotificationTier;
  values: { push: boolean; inAppBadge: boolean; email: boolean; sms: boolean; whatsapp: boolean };
  hasVerifiedPhone: boolean;
  // Whether WhatsApp reaches the user; the row is absent while the gate hides it.
  whatsapp: { reachable: boolean } | undefined;
  pendingKeys: ReadonlySet<string>;
  // The tier's master switch is off, so individual channels can't be changed.
  groupDisabled: boolean;
  onToggle: (tier: NotificationTier, channel: NotificationChannel, value: boolean) => void;
}) => {
  const isPending = (channel: NotificationChannel) =>
    groupDisabled || pendingKeys.has(`${tier}.${channel}`);
  return (
    <div className={groupDisabled ? "opacity-50" : undefined}>
      <SettingsRow
        title="Push"
        description="On your phone and in the browser."
        checked={values.push}
        disabled={isPending("push")}
        onCheckedChange={(next) => onToggle(tier, "push", next)}
      />
      <SettingsRow
        title="Badge"
        description="A count of what's waiting, on the app icon, the tab, and the chat panel."
        checked={values.inAppBadge}
        disabled={isPending("inAppBadge")}
        onCheckedChange={(next) => onToggle(tier, "inAppBadge", next)}
      />
      <SettingsRow
        title="Email"
        description="Reply to the email and it comes straight back to Okthx."
        checked={values.email}
        disabled={isPending("email")}
        onCheckedChange={(next) => onToggle(tier, "email", next)}
      />
      <SettingsRow
        title="SMS"
        description={
          hasVerifiedPhone
            ? "Text replies come straight back to Okthx."
            : "Verify your phone number to turn on SMS notifications."
        }
        checked={values.sms}
        disabled={groupDisabled || !hasVerifiedPhone || pendingKeys.has(`${tier}.sms`)}
        onCheckedChange={(next) => onToggle(tier, "sms", next)}
      />
      {whatsapp && (
        <SettingsRow
          title="WhatsApp"
          description={notificationWhatsappHint(whatsapp.reachable)}
          checked={values.whatsapp}
          // Locked only against turning on: one left on after WhatsApp was
          // unlinked can still be turned off.
          disabled={
            groupDisabled ||
            (!whatsapp.reachable && !values.whatsapp) ||
            pendingKeys.has(`${tier}.whatsapp`)
          }
          onCheckedChange={(next) => onToggle(tier, "whatsapp", next)}
        />
      )}
    </div>
  );
};

/** The calendar reminder SMS row's description, which suggests texts when push reaches nobody. */
const smsReminderDescription = ({
  hasVerifiedPhone,
  suggest,
}: {
  hasVerifiedPhone: boolean;
  suggest: boolean;
}) => {
  if (!hasVerifiedPhone) return "Verify your phone number to turn on SMS reminders.";
  if (suggest) return "Push reaches none of your devices. Get reminders as texts instead.";
  return "Get the reminder as a text.";
};

export const NotificationSettings = () => {
  const notifications = useSurfacePreferences("notifications");
  const updateNotificationPreferences = useUpdateSurfacePreferences("notifications");
  const { permission, requestPermission, disableNotifications, isSupported, isSubscribed } =
    useNotification();
  const { userContext } = useCurrentUserContext();
  // Usage alerts belong to the billing feature, which ships dark behind this
  // gate — the section hides with it, same as the Billing tab.
  const billingEnabled = useGateValue(BILLING_GATE);
  const reminderChannelsEnabled = useGateValue(REMINDER_CHANNELS_GATE);
  const queryClient = useQueryClient();
  // Track in-flight keys independently so concurrent toggles don't clobber
  // each other's pending state.
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const hasVerifiedPhone = !!userContext?.user.userContact?.handles?.find(
    (handle) => handle.type === "phoneNumber" && handle.verified,
  )?.value;
  // WhatsApp rows show with the gate; until the channel list arrives, or if it
  // fails, WhatsApp reads as reachable rather than locking the switch.
  const { data: reminderChannels } = useQuery({
    ...reminderQueries.channels(),
    enabled: reminderChannelsEnabled,
  });
  // Whether any push reaches the user, so a push-only reminder can say it reaches nobody.
  // Inside the iOS app's WebView the phone app is the push route and speaks for itself.
  const isWebView = typeof window !== "undefined" && window.isWebView === true;
  // Fresh on every visit: a phone may have turned push on since the app loaded.
  const { data: pushPrompt } = useQuery({
    ...pushPromptQuery("web"),
    enabled: !isWebView,
    staleTime: 0,
  });
  const noPushRoute = pushPrompt?.hasPushRoute === false;
  const whatsapp = reminderChannelsEnabled
    ? {
        reachable: isReminderChannelReachable("whatsapp", {
          hasVerifiedPhone,
          available: reminderChannels,
        }),
      }
    : undefined;

  // Quiet hours and the other top-level keys; tier channels go through
  // updateChannel so the delta nests under the tier.
  const updatePreference = async (
    key:
      | "escalation"
      | "escalationFirstDelay"
      | "escalationSecondDelay"
      | "quietHoursEnabled"
      | "quietHoursStart"
      | "quietHoursEnd",
    value: boolean | string,
  ) => {
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await updateNotificationPreferences({ [key]: value });
    } catch (_error) {
      errorToast("Failed to update notification preferences. Please try again.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const updateReminderChannel = async (channel: ReminderChannel, on: boolean) => {
    const pendingKey = `reminders.${channel}`;
    setPendingKeys((prev) => new Set(prev).add(pendingKey));
    try {
      await updateNotificationPreferences(reminderChannelDelta(channel, on));
    } catch (error) {
      Sentry.captureException(error);
      errorToast("Failed to update notification preferences. Please try again.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  const handleRoutineNotificationChannelChange = async (
    channel: RoutineNotificationChannel,
    on: boolean,
  ) => {
    const pendingKey = `routineNotifications.${channel}`;
    setPendingKeys((prev) => new Set(prev).add(pendingKey));
    try {
      await updateNotificationPreferences(routineNotificationDelta(channel, on));
    } catch (error) {
      Sentry.captureException(error);
      errorToast("Failed to update notification preferences. Please try again.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  // "In app" is a reminder channel only while a device takes push, and the
  // push-route hints read the same, so turning browser notifications on or off
  // refreshes both.
  const refreshPushRoutes = () => {
    void queryClient.invalidateQueries({ queryKey: reminderQueries.channels().queryKey });
    void queryClient.invalidateQueries({ queryKey: pushPromptQuery("web").queryKey });
  };

  const updateChannel = async (
    tier: NotificationTier,
    channel: NotificationTierKey,
    value: boolean,
  ) => {
    const pendingKey = `${tier}.${channel}`;
    setPendingKeys((prev) => new Set(prev).add(pendingKey));
    try {
      await updateNotificationPreferences({ [tier]: { [channel]: value } });
    } catch (_error) {
      errorToast("Failed to update notification preferences. Please try again.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  // Calendar reminder deltas nest under `calendarReminders`; `pendingKey`
  // tracks the specific control so concurrent toggles don't clobber each other.
  const updateCalendarReminder = async (
    pendingKey: string,
    value: (typeof notifications)["calendarReminders"],
  ) => {
    setPendingKeys((prev) => new Set(prev).add(pendingKey));
    try {
      await updateNotificationPreferences({ calendarReminders: value });
    } catch (_error) {
      errorToast("Failed to update notification preferences. Please try again.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  // Usage-alert deltas nest under `usageAlerts`, same shape as calendar
  // reminders. The master switch gates only the approaching-limit warning —
  // the exhausted notice always sends (per-channel toggles still apply).
  const updateUsageAlerts = async (
    pendingKey: string,
    value: (typeof notifications)["usageAlerts"],
  ) => {
    setPendingKeys((prev) => new Set(prev).add(pendingKey));
    try {
      await updateNotificationPreferences({ usageAlerts: value });
    } catch (_error) {
      errorToast("Failed to update notification preferences. Please try again.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  const handleEnableNotifications = async () => {
    setIsEnabling(true);
    try {
      await requestPermission();
    } finally {
      setIsEnabling(false);
      refreshPushRoutes();
    }
  };

  const handleDisableNotifications = async () => {
    setIsDisabling(true);
    try {
      await disableNotifications();
    } finally {
      setIsDisabling(false);
      refreshPushRoutes();
    }
  };

  const browserLoading = isEnabling || isDisabling;
  const browserDescription = "Get notified in this browser when Okthx needs you or has an update.";

  const renderBrowserSection = () => {
    if (!isSupported) return null;

    if (permission === "denied") return <BrowserDeniedRow />;

    if (permission === "granted") {
      return (
        <SettingsRow
          title="Browser notifications"
          description={browserDescription}
          checked={isSubscribed}
          disabled={browserLoading}
          onCheckedChange={(next) =>
            next ? handleEnableNotifications() : handleDisableNotifications()
          }
        />
      );
    }

    return (
      <SettingsRow
        title="Browser notifications"
        description={browserDescription}
        checked={false}
        disabled={browserLoading}
        onCheckedChange={() => handleEnableNotifications()}
      />
    );
  };

  return (
    <div>
      {TIER_SECTIONS.map(({ tier, title, description }) => {
        const values = notifications[tier];
        return (
          <div key={tier}>
            <SettingsRow
              title={title}
              description={description}
              checked={values.enabled}
              disabled={pendingKeys.has(`${tier}.enabled`)}
              onCheckedChange={(next) => updateChannel(tier, "enabled", next)}
            />
            <div className="pl-6">
              <TierChannels
                tier={tier}
                values={values}
                hasVerifiedPhone={hasVerifiedPhone}
                whatsapp={whatsapp}
                pendingKeys={pendingKeys}
                groupDisabled={!values.enabled}
                onToggle={updateChannel}
              />
            </div>
          </div>
        );
      })}
      {(() => {
        const cal = notifications.calendarReminders;
        const groupDisabled = !cal.enabled;
        // Push is the default and often the only channel on, so say when it reaches nobody.
        const pushReachesNobody = cal.enabled && cal.push && noPushRoute;
        return (
          <div>
            <SettingsRow
              title="Calendar reminders"
              description="Get a nudge when an event's reminder time arrives."
              checked={cal.enabled}
              disabled={pendingKeys.has("calendarReminders.enabled")}
              onCheckedChange={(next) =>
                updateCalendarReminder("calendarReminders.enabled", { ...cal, enabled: next })
              }
            />
            <div className="pl-6">
              <div className={groupDisabled ? "opacity-50" : undefined}>
                <SettingsRow
                  title="Push"
                  description={
                    pushReachesNobody
                      ? "Nothing gets these yet. Turn on browser notifications below or get the iPhone app."
                      : "On your phone and in the browser."
                  }
                  checked={cal.push}
                  disabled={groupDisabled || pendingKeys.has("calendarReminders.push")}
                  onCheckedChange={(next) =>
                    updateCalendarReminder("calendarReminders.push", { ...cal, push: next })
                  }
                />
                <SettingsRow
                  title="Email"
                  description="Get the reminder in your inbox."
                  checked={cal.email}
                  disabled={groupDisabled || pendingKeys.has("calendarReminders.email")}
                  onCheckedChange={(next) =>
                    updateCalendarReminder("calendarReminders.email", { ...cal, email: next })
                  }
                />
                <SettingsRow
                  title="SMS"
                  description={smsReminderDescription({
                    hasVerifiedPhone,
                    suggest: pushReachesNobody && !cal.sms,
                  })}
                  checked={cal.sms}
                  disabled={
                    groupDisabled || !hasVerifiedPhone || pendingKeys.has("calendarReminders.sms")
                  }
                  onCheckedChange={(next) =>
                    updateCalendarReminder("calendarReminders.sms", { ...cal, sms: next })
                  }
                />
                {whatsapp && (
                  <SettingsRow
                    title="WhatsApp"
                    description={notificationWhatsappHint(whatsapp.reachable)}
                    checked={cal.whatsapp}
                    disabled={
                      groupDisabled ||
                      (!whatsapp.reachable && !cal.whatsapp) ||
                      pendingKeys.has("calendarReminders.whatsapp")
                    }
                    onCheckedChange={(next) =>
                      updateCalendarReminder("calendarReminders.whatsapp", {
                        ...cal,
                        whatsapp: next,
                      })
                    }
                  />
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {reminderChannelsEnabled && (
        <ReminderChannelRows
          notifications={notifications}
          hasVerifiedPhone={hasVerifiedPhone}
          pendingKeys={pendingKeys}
          onToggle={updateReminderChannel}
        />
      )}
      <RoutineNotificationRows
        notifications={notifications}
        pendingKeys={pendingKeys}
        onToggle={handleRoutineNotificationChannelChange}
      />
      {billingEnabled &&
        (() => {
          const usage = notifications.usageAlerts;
          return (
            <fieldset aria-label="Usage alerts">
              <SettingsRow
                title="Usage alerts"
                description="Warn me when I'm close to my plan's monthly usage limit. If I ever have to pause at the limit, you'll hear about that regardless."
                checked={usage.enabled}
                disabled={pendingKeys.has("usageAlerts.enabled")}
                onCheckedChange={(next) =>
                  updateUsageAlerts("usageAlerts.enabled", { ...usage, enabled: next })
                }
              />
              {/* Deliberately not greyed out when the master is off: the master
                gates only the approaching-limit warning, while these channels
                also govern the always-sent paused-at-limit notice. */}
              <div className="pl-6">
                <SettingsRow
                  title="Push"
                  description="On your phone and in the browser."
                  checked={usage.push}
                  disabled={pendingKeys.has("usageAlerts.push")}
                  onCheckedChange={(next) =>
                    updateUsageAlerts("usageAlerts.push", { ...usage, push: next })
                  }
                />
                <SettingsRow
                  title="Email"
                  description="Get the alert in your inbox."
                  checked={usage.email}
                  disabled={pendingKeys.has("usageAlerts.email")}
                  onCheckedChange={(next) =>
                    updateUsageAlerts("usageAlerts.email", { ...usage, email: next })
                  }
                />
                <SettingsRow
                  title="SMS"
                  description={
                    hasVerifiedPhone
                      ? "Get the alert as a text."
                      : "Verify your phone number to turn on SMS alerts."
                  }
                  checked={usage.sms}
                  disabled={!hasVerifiedPhone || pendingKeys.has("usageAlerts.sms")}
                  onCheckedChange={(next) =>
                    updateUsageAlerts("usageAlerts.sms", { ...usage, sms: next })
                  }
                />
              </div>
            </fieldset>
          );
        })()}
      <SettingsRow
        title="Follow-up reminders"
        description="If something needs you and you haven't responded, I'll follow up with a friendly text."
        checked={notifications.escalation}
        disabled={pendingKeys.has("escalation")}
        onCheckedChange={(next) => updatePreference("escalation", next)}
      />
      {notifications.escalation && (
        <EscalationDelayRow
          first={notifications.escalationFirstDelay}
          second={notifications.escalationSecondDelay}
          firstPending={pendingKeys.has("escalationFirstDelay")}
          secondPending={pendingKeys.has("escalationSecondDelay")}
          onCommit={updatePreference}
        />
      )}
      <SettingsRow
        title="Quiet hours"
        description="Hold push and texts overnight."
        checked={notifications.quietHoursEnabled}
        disabled={pendingKeys.has("quietHoursEnabled")}
        onCheckedChange={(next) => updatePreference("quietHoursEnabled", next)}
      />
      {notifications.quietHoursEnabled && (
        <QuietHoursRow
          start={notifications.quietHoursStart}
          end={notifications.quietHoursEnd}
          startPending={pendingKeys.has("quietHoursStart")}
          endPending={pendingKeys.has("quietHoursEnd")}
          onCommit={updatePreference}
        />
      )}
      {renderBrowserSection()}
      {!isWebView && canGetIphoneApp() && (
        <SettingsRow
          title="Okthx for iPhone"
          description="Get reminders and updates on your phone."
          control={<GetIphoneApp qrSize={96} />}
        />
      )}
      <EmailSubscriptions />
    </div>
  );
};
